import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { assetHoldings, marginPositions, futuresPositions } from '../../utils/portfolioData';
// Spot Holdings Card with Navigation
function SpotHoldingCard() {
  const [currentIndex, setCurrentIndex] = useState(0);
  // Sort by value (amount * price) descending
  const sortedHoldings = [...assetHoldings].filter(h => h.spotAmount > 0).sort((a, b) => b.spotAmount * b.price - a.spotAmount * a.price);
  if (sortedHoldings.length === 0) return null;
  const holding = sortedHoldings[currentIndex];
  const value = holding.spotAmount * holding.price;
  const handlePrev = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : sortedHoldings.length - 1);
  };
  const handleNext = () => {
    setCurrentIndex(prev => prev < sortedHoldings.length - 1 ? prev + 1 : 0);
  };
  return <div className="bg-background-secondary border border-border-subtle rounded-[16px] p-3 flex flex-col gap-2" data-id="element-604">
      <div className="flex items-center justify-between" data-id="element-605">
        <span className="text-[10px] text-text-secondary font-label uppercase tracking-wider" data-id="element-606">
          Spot Holding
        </span>
        {sortedHoldings.length > 1 && <div className="flex items-center gap-1" data-id="element-607">
            <button onClick={handlePrev} className="p-0.5 hover:bg-background-elevated rounded transition-colors text-text-secondary hover:text-text-primary" data-id="element-608">
              <ChevronLeft size={14} data-id="element-609" />
            </button>
            <span className="text-[10px] text-text-tertiary font-mono" data-id="element-610">
              {currentIndex + 1}/{sortedHoldings.length}
            </span>
            <button onClick={handleNext} className="p-0.5 hover:bg-background-elevated rounded transition-colors text-text-secondary hover:text-text-primary" data-id="element-611">
              <ChevronRight size={14} data-id="element-612" />
            </button>
          </div>}
      </div>

      <div className="flex items-center gap-2" data-id="element-613">
        <img src={holding.logo} alt={holding.symbol} className="w-6 h-6 object-contain" data-id="element-614" />
        <div className="flex-1 min-w-0" data-id="element-615">
          <div className="text-sm font-semibold text-text-primary" data-id="element-616">
            {holding.symbol}
          </div>
          <div className="text-[10px] text-text-secondary" data-id="element-617">{holding.name}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border-subtle" data-id="element-618">
        <div data-id="element-619">
          <div className="text-[10px] text-text-secondary font-label mb-0.5 text-left" data-id="element-620">
            Amount
          </div>
          <div className="text-xs font-numbers text-text-primary text-left" data-id="element-621">
            {holding.spotAmount.toLocaleString('en-US', {
            minimumFractionDigits: holding.symbol === 'USDT' ? 2 : 4,
            maximumFractionDigits: holding.symbol === 'USDT' ? 2 : 4
          })}
          </div>
        </div>
        <div data-id="element-622">
          <div className="text-[10px] text-text-secondary font-label mb-0.5 text-right" data-id="element-623">
            Value
          </div>
          <div className="text-xs font-numbers text-text-primary font-medium text-right" data-id="element-624">
            {value.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}{' '}
            <span className="text-text-secondary" data-id="element-625">USDT</span>
          </div>
        </div>
      </div>
    </div>;
}
// Margin Position Card with Navigation
function MarginPositionCard() {
  const [currentIndex, setCurrentIndex] = useState(0);
  if (marginPositions.length === 0) return null;
  const position = marginPositions[currentIndex];
  const handlePrev = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : marginPositions.length - 1);
  };
  const handleNext = () => {
    setCurrentIndex(prev => prev < marginPositions.length - 1 ? prev + 1 : 0);
  };
  return <div className="bg-background-secondary border border-border-subtle rounded-[16px] p-3 flex flex-col gap-2" data-id="element-626">
      <div className="flex items-center justify-between" data-id="element-627">
        <span className="text-[10px] text-text-secondary font-label uppercase tracking-wider" data-id="element-628">
          Margin Position
        </span>
        <button className="text-[10px] text-brand-purple hover:text-brand-purple-light transition-colors font-medium flex items-center gap-0.5" data-id="element-629">
          Manage
          <ExternalLink size={10} data-id="element-630" />
        </button>
      </div>

      <div className="flex items-center gap-2" data-id="element-631">
        <img src={position.logo} alt={position.symbol} className="w-6 h-6 object-contain" data-id="element-632" />
        <div className="flex-1 min-w-0 flex items-center gap-2" data-id="element-633">
          <span className="text-sm font-semibold text-text-primary" data-id="element-634">
            {position.market}
          </span>
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${position.side === 'Long' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`} data-id="element-635">
            {position.side}
          </span>
          <span className="text-[10px] text-text-tertiary bg-background-elevated px-1.5 py-0.5 rounded" data-id="element-636">
            {position.leverage}x
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border-subtle" data-id="element-637">
        <div data-id="element-638">
          <div className="text-[10px] text-text-secondary font-label mb-0.5 text-left" data-id="element-639">
            Size
          </div>
          <div className="text-xs font-numbers text-text-primary text-left" data-id="element-640">
            {position.openQuantity.toFixed(8)} {position.symbol}
          </div>
        </div>
        <div data-id="element-641">
          <div className="text-[10px] text-text-secondary font-label mb-0.5 text-right" data-id="element-642">
            UP&L
          </div>
          <div className="text-right" data-id="element-643">
            <div className={`text-xs font-numbers font-medium ${position.unrealizedPnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-644">
              {position.unrealizedPnl >= 0 ? '+' : ''}
              {position.unrealizedPnl.toFixed(2)}{' '}
              <span className="text-text-secondary" data-id="element-645">USDT</span>
            </div>
            <div className="text-[10px] text-text-secondary" data-id="element-646">
              ({position.unrealizedPnlPercent >= 0 ? '+' : ''}
              {position.unrealizedPnlPercent.toFixed(2)}%)
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// Futures Position Card with Navigation
function FuturesPositionCard() {
  const [currentIndex, setCurrentIndex] = useState(0);
  if (futuresPositions.length === 0) return null;
  const position = futuresPositions[currentIndex];
  const handlePrev = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : futuresPositions.length - 1);
  };
  const handleNext = () => {
    setCurrentIndex(prev => prev < futuresPositions.length - 1 ? prev + 1 : 0);
  };
  return <div className="bg-background-secondary border border-border-subtle rounded-[16px] p-3 flex flex-col gap-2" data-id="element-647">
      <div className="flex items-center justify-between" data-id="element-648">
        <span className="text-[10px] text-text-secondary font-label uppercase tracking-wider" data-id="element-649">
          Futures Position
        </span>
        <button className="text-[10px] text-brand-purple hover:text-brand-purple-light transition-colors font-medium flex items-center gap-0.5" data-id="element-650">
          Manage
          <ExternalLink size={10} data-id="element-651" />
        </button>
      </div>

      <div className="flex items-center gap-2" data-id="element-652">
        <img src={position.logo} alt={position.symbol} className="w-6 h-6 object-contain" data-id="element-653" />
        <div className="flex-1 min-w-0 flex items-center gap-2" data-id="element-654">
          <span className="text-sm font-semibold text-text-primary" data-id="element-655">
            {position.pair}
          </span>
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${position.side === 'Long' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`} data-id="element-656">
            {position.side}
          </span>
          <span className="text-[10px] text-text-tertiary bg-background-elevated px-1.5 py-0.5 rounded" data-id="element-657">
            {position.leverage}x
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border-subtle" data-id="element-658">
        <div data-id="element-659">
          <div className="text-[10px] text-text-secondary font-label mb-0.5 text-left" data-id="element-660">
            Size
          </div>
          <div className="text-xs font-numbers text-text-primary text-left" data-id="element-661">
            {position.size} {position.symbol}
          </div>
        </div>
        <div data-id="element-662">
          <div className="text-[10px] text-text-secondary font-label mb-0.5 text-right" data-id="element-663">
            UP&L
          </div>
          <div className="text-right" data-id="element-664">
            <div className={`text-xs font-numbers font-medium ${position.unrealizedPnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-665">
              {position.unrealizedPnl >= 0 ? '+' : ''}
              {position.unrealizedPnl.toFixed(2)}{' '}
              <span className="text-text-secondary" data-id="element-666">USDT</span>
            </div>
            <div className="text-[10px] text-text-secondary" data-id="element-667">
              ({position.unrealizedPnlPercent >= 0 ? '+' : ''}
              {position.unrealizedPnlPercent.toFixed(2)}%)
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// Earn Position Card (placeholder - no data yet)
function EarnPositionCard() {
  // Mock earn positions - in real app this would come from portfolioData
  const earnPositions: any[] = [];
  if (earnPositions.length === 0) return null;
  return <div className="bg-background-secondary border border-border-subtle rounded-[16px] p-3 flex flex-col gap-2" data-id="element-668">
      <div className="flex items-center justify-between" data-id="element-669">
        <span className="text-[10px] text-text-secondary font-label uppercase tracking-wider" data-id="element-670">
          Earn Position
        </span>
      </div>
      {/* Earn position content */}
    </div>;
}
export function StatsCards() {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-671">
      <SpotHoldingCard data-id="element-672" />
      <MarginPositionCard data-id="element-673" />
      <FuturesPositionCard data-id="element-674" />
      <EarnPositionCard data-id="element-675" />
    </div>;
}